import {
  AppFunction,
  AppRole,
  CareInstitution,
  Case,
  Category,
  Department,
  DepartmentRole,
  FunctionRolePermission,
  ListColumnConfig,
  Patient,
  PatientListModel,
  Profile,
} from '@/models';
import {
  AppFunctionService,
  AuthService,
  CareInstitutionService,
  CaseService,
  CategoryService,
  DataService,
  DepartmentService,
  ErrorService,
  ImageService,
  LanguageService,
  NotificationService,
  PatientService,
  ProfileSetDataService,
  ProfileSetUtilsService,
  StorageService,
} from '@/services';
import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDrawer } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  AppSidenavComponent,
  SidenavNavGroup,
  SidenavNavItem,
  VoucherRequiredDialogService,
} from 'src/app/shared/components';

@Component({
  selector: 'app-epd',
  templateUrl: './epd.component.html',
  styleUrls: ['./epd.component.scss'],
  standalone: false,
})
export class EpdComponent implements OnInit, OnDestroy, AfterViewInit {
  /** Reference to the shared sidenav shell – used to open/close/toggle the EPD drawer. */
  @ViewChild(AppSidenavComponent) sidenav: AppSidenavComponent;
  /** Secondary patient-picker overlay drawer – EPD-specific, not part of the shared shell. */
  @ViewChild('patientDrawer') patientDrawer: MatDrawer;

  patientDrawerOpen: boolean = false;
  private profileActiveSubscription: Subscription;
  private activePatientSubscription: Subscription;
  private updateSideMenuSubscription: Subscription;
  private epdDrawerToggleSubscription: Subscription;
  private profileSubscription: Subscription;

  departmentPatients: PatientListModel[];
  patientDrawerColumns: ListColumnConfig[] = [];
  patientDrawerTableDisabeld: boolean = false;

  searching: boolean = false;

  aiChatButtonVisible: boolean = false;

  patientDrawerDepartmentColumns: ListColumnConfig[] = [];
  patientDrawerAllColumns: ListColumnConfig[] = [];
  toggleButtonData: ListColumnConfig[];
  patientDrawerSelectedToggle: string = 'department';
  loading: boolean = false;
  functionsLoading: boolean = false;
  profileLoading: boolean = false;
  profileActive: boolean = false;

  careInstitutions: CareInstitution[] = [];
  departments: Department[] = [];
  departmentRoles: DepartmentRole[] = [];

  activeCareInstitution?: CareInstitution | null;
  activeDepartment: Department | null;
  activeDepartmentRole: DepartmentRole | null;
  activePatient: Patient | null;

  categories: Category[] = [];
  categoriesVisible: boolean = false;
  searchQuery: string = '';
  searchedCategories: Category[] = [];

  profile: Profile = {};
  profileForm = new UntypedFormGroup({
    institutionControl: new UntypedFormControl('', [Validators.required]),
    departmentControl: new UntypedFormControl('', [Validators.required]),
    departmentRoleControl: new UntypedFormControl('', [Validators.required]),
  });

  constructor(
    private authService: AuthService,
    private router: Router,
    private dataService: DataService,
    private storage: StorageService,
    private departmentService: DepartmentService,
    private careInstitutionService: CareInstitutionService,
    private notificationService: NotificationService,
    private cd: ChangeDetectorRef,
    private appFunctionService: AppFunctionService,
    private categoryService: CategoryService,
    private patientService: PatientService,
    private errorService: ErrorService,
    private profileSetUtils: ProfileSetUtilsService,
    private profileSetData: ProfileSetDataService,
    private imageService: ImageService,
    private languageService: LanguageService,
    private caseService: CaseService,
    private voucherRequiredDialogService: VoucherRequiredDialogService
  ) {
    this.setListColumnConfigs();
  }

  private setListColumnConfigs() {
    this.patientDrawerDepartmentColumns = [
      {
        id: 'name',
        name: this.languageService.translate('general.words.name'),
        width: '70%',
      },
      {
        id: 'dayOfBirthEpoch',
        date: true,
        name: this.languageService.translate('general.words.birthdate'),
        width: '30%',
      },
    ];
    this.patientDrawerAllColumns = [
      {
        id: 'name',
        name: this.languageService.translate('general.words.name'),
        width: '25%',
      },
      {
        id: 'dayOfBirthEpoch',
        date: true,
        name: this.languageService.translate('general.words.birthdate'),
        width: '15%',
      },
      {
        id: 'careInstitutionName',
        name: this.languageService.translate('general.words.organization'),
        width: '20%',
      },
      {
        id: 'departmentName',
        name: this.languageService.translate('general.words.department'),
        width: '20%',
      },
    ];
  }

  ngOnInit(): void {
    this.careInstitutionService.getCareInstitutions().subscribe(
      resp => {
        var careInstitutions = (resp as CareInstitution[]).sort((a, b) => a.name.localeCompare(b.name));
        for (let c of careInstitutions) {
          if (c.departments) {
            c.departments.sort((a, b) => a.name.localeCompare(b.name));
            for (let d of c.departments) {
              if (d.roles) {
                d.roles.sort((a, b) => a.name.localeCompare(b.name));
              }
            }
          }
        }
        this.careInstitutions = careInstitutions;
      },
      err => this.errorService.onError(err)
    );

    this.updateSideMenuSubscription = this.dataService.updateSideMenuValue.subscribe(() => {
      void this.loadRegistrationCounts();
    });

    if (this.activePatientSubscription) {
      this.activePatientSubscription.unsubscribe();
    }
    this.activePatientSubscription = this.dataService.activePatient.subscribe(resp => {
      if (resp && resp.id) {
        if (this.activePatient?.id === resp.id) {
          this.refreshActivePatientProperties(resp as Patient);
          return;
        }
        this.setActiveData(new PatientListModel(resp as Patient), false);
        if ((resp as Patient) && (resp as Patient).id) {
          this.categoriesVisible = true;
          this.setDashboardNavItem();
          this.setNavGroups();
          this.checkAiChatButtonVisibility();
        }
      }
    });

    this.setToggleButtonData();
  }

  ngAfterViewInit(): void {
    // Subscriptions that need the shared sidenav reference are set up here.
    this.profileSubscription = this.profileSetData.activeProfile.subscribe((profile: Profile) => {
      if (this.sidenav.isDrawerOpen) this.handleProfileChange(profile);
    });

    this.profileActiveSubscription = this.profileSetData.profileActive.subscribe(profileActive => {
      this.profileActive = profileActive;
      if (this.profileActive) {
        this.sidenav.openDrawer().then(() => {
          this.setProfile();
        });
        this.cd.detectChanges();
      } else {
        this.router.navigateByUrl('/epd');
      }
    });

    // dataService.epdDrawerToggle also needs the sidenav reference.
    this.epdDrawerToggleSubscription = this.dataService.epdDrawerToggle.subscribe(toggleRequest => {
      if (!toggleRequest) {
        return;
      }
      this.sidenav.toggleDrawer();
    });
  }

  setProfile() {
    this.profileLoading = true;
    const profile = this.profileSetUtils.getLocalStoredProfile();
    if (!profile.institutionId) {
      this.profileLoading = false;
      return;
    }
    this.careInstitutionService.getCareInstitutionById(profile.institutionId).subscribe(
      resp => {
        let ci = resp as CareInstitution;
        this.activeCareInstitution = ci;
        this.departments = ci.departments.sort((a, b) => a.name.localeCompare(b.name));
        this.activeDepartment = ci.departments.find(x => x.id == profile.departmentId) as Department;
        this.departmentRoles = this.activeDepartment.roles.sort((a, b) => a.name.localeCompare(b.name));
        this.activeDepartmentRole = this.activeDepartment.roles.find(
          x => x.id == profile.departmentRoleId
        ) as DepartmentRole;
        this.profileForm.controls['institutionControl'].setValue(this.activeCareInstitution?.id);
        this.profileForm.controls['departmentControl'].setValue(this.activeDepartment?.id);
        this.profileForm.controls['departmentRoleControl'].setValue(this.activeDepartmentRole?.id);
        this.profileLoading = false;
      },
      err => {
        this.profileLoading = false;
        this.errorService.onError(err);
      }
    );
  }

  private setToggleButtonData() {
    this.patientDrawerSelectedToggle = 'department';
    this.toggleButtonData = [
      {
        id: 'department',
        width: '0',
        name: this.languageService.translate('general.words.department'),
        selected: true,
      },
      {
        id: 'all',
        width: '0',
        name: this.languageService.translate('epd.menu.all-patient', {
          PATIENT_LABEL: this.activeCareInstitution?.patientTitlePlural,
        }),
      },
    ];
  }

  ngOnDestroy(): void {
    this.profileActiveSubscription?.unsubscribe();
    this.profileSubscription?.unsubscribe();
    this.activePatientSubscription?.unsubscribe();
    this.updateSideMenuSubscription?.unsubscribe();
    this.epdDrawerToggleSubscription?.unsubscribe();
  }

  toggleDrawer(): void {
    this.sidenav.toggleDrawer();
  }

  closeDrawer(): void {
    this.sidenav.closeDrawerIfMobile();
  }

  openPage(routerUrl: string, functionId: string, voucherRequired: boolean = false, hasAsyncGuard: boolean = false) {
    if (voucherRequired) {
      this.voucherRequiredDialogService.open();
      return;
    }
    if (!this.activePatient) {
      this.notificationService.warning(
        this.languageService.translateWithCap('epd.menu.notifications.select-patient', {
          PATIENT_LABEL: this.activeCareInstitution?.patientTitleSingular,
        }),
        this.languageService.translateWithCap('epd.menu.notifications.no-patient-selected', {
          PATIENT_LABEL: this.activeCareInstitution?.patientTitleSingular,
        })
      );
      return;
    }
    if (!this.activeDepartmentRole) {
      this.notificationService.warning(
        this.languageService.translateWithCap('epd.menu.notifications.select-role'),
        this.languageService.translateWithCap('epd.menu.notifications.no-role-selected')
      );
      return;
    }
    if (functionId != '-1') {
      this.appFunctionService.getAppFunctionPermissions(functionId, this.activeDepartmentRole!.id).subscribe(
        resp => {
          var found = false;
          var permissions = resp as FunctionRolePermission[];
          this.appFunctionService.setCurrentAppFunctionPermissions(permissions);
          for (var perm of permissions) {
            if (perm.permission.title == 'lezen') {
              found = true;
            }
          }
          if (this.authService.isUserAdmin()) {
            found = true;
          }
          if (found) {
            this.closeDrawer();
            if (hasAsyncGuard) {
              // The '/epd' -> skipLocationChange -> routerUrl reload dance below routes through
              // ProfileSetComponent and EpdEmptyComponent, which both synchronously navigate
              // onward (to 'epd/empty' / the dashboard) whenever a patient is already active.
              // That cascade races the final navigateByUrl and can cancel it mid-flight,
              // silently aborting any in-flight HTTP call from an async canActivate guard (e.g.
              // voucherGuard). Every other route's guards are synchronous, so this never showed
              // up before. Navigate directly instead for routes known to carry an async guard.
              this.router.navigateByUrl(routerUrl);
            } else {
              this.router.navigateByUrl('/epd', { skipLocationChange: true }).then(() => {
                this.router.navigateByUrl(routerUrl);
              });
            }
          } else {
            this.notificationService.warning(
              this.languageService.translateWithCap('epd.menu.notifications.role-insufficient-rights', {
                ROLE: this.activeDepartmentRole!.name,
              }),
              this.languageService.translateWithCap('general.inputs.errors.no-permission')
            );
          }
        },
        err => this.errorService.onError(err)
      );
    } else {
      this.router.navigateByUrl('/epd', { skipLocationChange: true }).then(() => {
        this.router.navigateByUrl(routerUrl);
      });
    }
  }

  closePatientDrawer() {
    this.patientDrawer.close();
  }

  onInstitutionChange = (institution: CareInstitution) =>
    this.profileSetUtils.onInstitutionChange(this.profile, institution.id);

  onDepartmentChange = (department: Department) => this.profileSetUtils.onDepartmentChange(this.profile, department.id);

  onDepartmentRoleChange = (departmentRole: DepartmentRole) =>
    this.profileSetUtils.onDepartmentRoleChange(this.profile, departmentRole.id);

  async handleProfileChange(newProfile: Profile) {
    if (newProfile.institutionId && this.profile.institutionId !== newProfile.institutionId) {
      this.activeCareInstitution = await this.careInstitutionService.getCareInstitutionForId(newProfile.institutionId);
      if (this.activeCareInstitution) {
        this.departments = this.activeCareInstitution.departments.sort((a, b) => a.name.localeCompare(b.name));
      }
      this.activeDepartment = null;
      this.activeDepartmentRole = null;
      this.categoriesVisible = false;
      this.activePatient = null;
      this.dataService.changeActivePatient(new Patient());
      this.departmentRoles = [];
      this.router.navigateByUrl('epd');
    } else if (newProfile.departmentId && this.profile.departmentId !== newProfile.departmentId) {
      this.activeDepartment =
        this.activeCareInstitution?.departments.find(d => d.id == newProfile.departmentId) || null;
      this.departmentRoles = this.activeDepartment?.roles.sort((a, b) => a.name.localeCompare(b.name)) || [];
      this.categoriesVisible = true;
      this.activeDepartmentRole = null;
      this.activePatient = null;
      this.dataService.changeActivePatient(new Patient());
      this.router.navigateByUrl('epd');
    } else if (newProfile.departmentRoleId && this.profile.departmentRoleId !== newProfile.departmentRoleId) {
      this.activeDepartmentRole = this.activeDepartment?.roles.find(r => r.id == newProfile.departmentRoleId) || null;
      this.router.navigateByUrl('epd');
    }
    this.profile = newProfile;
    this.setDashboardNavItem();
    this.setNavGroups();
  }

  openPatientDrawer() {
    this.patientDrawerColumns = this.patientDrawerDepartmentColumns;
    if (!this.patientDrawer.opened) {
      if (this.activeDepartment?.id) {
        this.loading = true;
        this.patientDrawer.toggle();
        this.departmentService.getPatientsForDepartment(this.activeDepartment?.id).subscribe(
          resp => {
            var patients = resp as Patient[];
            this.departmentPatients = [];
            for (let p of patients) {
              this.departmentPatients.push(new PatientListModel(p));
            }
            this.departmentPatients.sort((a, b) => a.name.localeCompare(b.name));
            this.loading = false;
            this.setToggleButtonData();
          },
          err => this.errorService.onError(err)
        );
      } else {
        this.notificationService.warning(
          this.languageService.translateWithCap('epd.menu.notifications.select-department'),
          ''
        );
      }
    }
  }

  filterNavItems(event: any) {
    this.searching = true;
    if (!this.searchQuery) {
      this.searching = false;
      this.searchedCategories = this.categories;
      this.setNavGroups();
      return;
    }
    let tempSearchedCats = [];
    for (let cat of this.categories) {
      let foundFuncs = cat.categoryAppFunctions.filter(x =>
        x.appFunction.title.toLocaleLowerCase().includes(this.searchQuery.toLocaleLowerCase())
      );
      for (let func of foundFuncs) {
        let alreadyPresent = tempSearchedCats.find(x => x.id === cat.id);
        if (!alreadyPresent) {
          var category = JSON.parse(JSON.stringify(this.getCategoryForAppFunctionId(func.id))) as Category;
          category.categoryAppFunctions = [];
          category.categoryAppFunctions.push(func);
          tempSearchedCats.push(category);
        } else {
          var funcAlreadyPresent = alreadyPresent.categoryAppFunctions.find(x => x.id === func.id);
          if (!funcAlreadyPresent) {
            alreadyPresent.categoryAppFunctions.push(func);
          }
        }
      }
    }
    this.searchedCategories = tempSearchedCats;
    this.setNavGroups();
  }

  private getCategoryForAppFunctionId(appFuncId: string): Category {
    for (let cat of this.categories) {
      var foundFunc = cat.categoryAppFunctions.find(x => x.id == appFuncId);
      if (foundFunc) {
        return cat;
      }
    }
    return new Category();
  }

  clearSearchQuery() {
    this.searchQuery = '';
    this.filterNavItems(null);
  }

  getImgUrl(): string {
    var imgId = this.activePatient?.properties.find(x => x.property.name == 'foto')?.value;
    if (imgId) {
      return this.imageService.buildImageUrl(imgId);
    } else {
      return 'icon';
    }
  }

  async onRowClick(event: any) {
    if (this.authService.isUserUser()) {
      const { data, error } = await this.patientService.isAllowed(event.id);
      if (error) {
        return;
      }
      if (data?.allowed) {
        this.setActiveData(event, true);
      } else {
        this.notificationService.warning(
          this.languageService.translateWithCap('epd.menu.notifications.no-patient-permission', {
            PATIENT_LABEL: event.patient.department.careInstitution.patientTitleSingular,
          }),
          this.languageService.translateWithCap('general.inputs.errors.no-permission')
        );
      }
    } else {
      this.setActiveData(event, true);
    }
  }

  private refreshActivePatientProperties(patient: Patient) {
    if (!this.activePatient) return;
    this.activePatient.properties = patient.properties;
    const drawerIndex = this.departmentPatients?.findIndex(x => x.id === patient.id) ?? -1;
    if (drawerIndex >= 0) {
      const drawerPatient = this.departmentPatients[drawerIndex].patient;
      drawerPatient.properties = patient.properties;
      this.departmentPatients[drawerIndex] = new PatientListModel(drawerPatient);
    }
  }

  setActiveData(event: any, updateSub: boolean) {
    let p = (event as PatientListModel).patient as Patient;

    if (!this.activeCareInstitution) {
      if (p && p.department && p.department.careInstitution) {
        this.activeCareInstitution = p.department.careInstitution;
      } else {
        this.activeCareInstitution = new CareInstitution(this.storage.getItem('activeCareInstitution')!, '');
      }
    }
    this.activePatient = new Patient();
    this.activePatient.id = p.id;
    this.activePatient.properties = p.properties;
    if (p.department) {
      var curCareInstitutionId = this.profileForm.get('institutionControl')?.value;
      if (curCareInstitutionId != p.department.careInstitution.id) {
        this.profileForm.get('institutionControl')?.setValue(p.department.careInstitution.id);
        if (this.activeCareInstitution && this.activeCareInstitution.departments) {
          this.departments = this.activeCareInstitution.departments.sort((a, b) => a.name.localeCompare(b.name));
        }
      }
      var curDepartmentId = this.profileForm.get('departmentControl')?.value;
      if (curDepartmentId != p.department.id) {
        this.profileForm.get('departmentControl')?.setValue(p.department.id);
        this.profileForm.get('departmentRoleControl')?.setValue(null);
      }
    }
    if (updateSub) {
      // changeActivePatient re-enters via the activePatient subscription (updateSub = false),
      // which does the getSideMenu + navigation below — running it here too doubles everything.
      this.dataService.changeActivePatient(this.activePatient);
      this.patientDrawer.toggle();
      return;
    }
    this.getSideMenu();
    this.router.navigateByUrl(`/epd/patient/${p.id}/dashboard`);
  }

  getSideMenu() {
    if (this.activeCareInstitution?.id) {
      this.categoriesVisible = true;
      this.categories = [];
      this.functionsLoading = true;
      this.categoryService.getCategoriesForInstitutionId(this.activeCareInstitution.id).subscribe({
        next: resp => {
          this.categories = resp as Category[];
          this.functionsLoading = false;
          for (let category of this.categories) {
            category.categoryAppFunctions.sort((a, b) => a.order - b.order);
          }
          this.searchedCategories = this.categories;
          this.setNavGroups();
          void this.loadRegistrationCounts();
        },
        error: err => {
          this.functionsLoading = false;
          this.errorService.onError(err);
        },
      });
    }
  }

  private async loadRegistrationCounts(): Promise<void> {
    if (!this.activePatient?.id) {
      return;
    }
    const { data } = await this.patientService.getRegistrationCounts(this.activePatient.id);
    const counts = data?.counts;
    if (!counts) {
      return;
    }
    for (const category of this.categories) {
      for (const appFunc of category.categoryAppFunctions) {
        appFunc.registrationAmount = counts[appFunc.appFunction.id] ?? 0;
      }
    }
    this.setNavGroups();
  }

  /** Returns a single-item array for the dashboard nav entry, or [] when no patient is selected. */
  dashboardNavItem: SidenavNavItem[];
  navGroups: SidenavNavGroup[];

  setDashboardNavItem() {
    if (!this.activePatient) {
      this.dashboardNavItem = [];
      return;
    }
    const patientId = this.activePatient.id;
    this.dashboardNavItem = [
      {
        label: 'epd.menu.dashboard',
        path: `/epd/patient/${patientId}/dashboard`,
        cssClass: 'dashboard-nav-list-item',
        order: 0,
        onClick: () => this.openPage(`epd/patient/${patientId}/dashboard`, '-1'),
        actions: [
          {
            icon: 'print',
            activePath: `/epd/patient/${patientId}/print`,
            onClick: (e: MouseEvent) => this.openPrint(e),
          },
        ],
      },
    ];
  }

  /**
   * Returns all function categories as accordion groups.
   * Returns [] when no patient is selected or categories are not yet visible.
   */
  setNavGroups() {
    if (!this.activePatient || !this.categoriesVisible || !this.searchedCategories?.length) {
      this.navGroups = [];
      return;
    }
    this.navGroups = [...this.searchedCategories]
      .sort((a, b) => a.order - b.order)
      .map(category => ({
        label: category.name,
        items: this.getCategoryNavItems(category),
      }));
  }

  /** Returns nav items for one function-category accordion panel. */
  getCategoryNavItems(category: Category): SidenavNavItem[] {
    if (!this.activePatient) return [];
    const patientId = this.activePatient.id;
    return category.categoryAppFunctions.map(f => ({
      label: f.appFunction.title,
      path: `/epd/patient/${patientId}/${this.insertIdIntoFunctionPath(f.appFunction)}`,
      cssClass: 'function-list-item',
      badge: f.registrationAmount,
      order: f.appFunction.order,
      onClick: () => {
        this.openPage(
          `epd/patient/${patientId}/${this.insertIdIntoFunctionPath(f.appFunction)}`,
          f.appFunction.id,
          f.appFunction.voucherRequired,
          !!f.appFunction.featureFlag
        );
      },
    }));
  }

  insertIdIntoFunctionPath(appFunction: AppFunction): string {
    if (appFunction.endpoint.includes('{id}')) {
      return appFunction.endpoint.replace('{id}', appFunction.id);
    }
    return appFunction.endpoint;
  }

  onToggleButtonChange(event: any) {
    switch (event) {
      case 'all':
        this.loading = true;
        this.patientDrawerTableDisabeld = true;
        void this.loadAllPatients();
        break;
      case 'department':
        this.loading = true;
        this.patientDrawerTableDisabeld = true;
        if (this.activeDepartment?.id) {
          this.departmentService.getPatientsForDepartment(this.activeDepartment?.id).subscribe(
            resp => {
              this.patientDrawerColumns = this.patientDrawerDepartmentColumns;
              var patients = resp as Patient[];
              this.departmentPatients = [];
              for (let p of patients) {
                this.departmentPatients.push(new PatientListModel(p));
              }
              this.departmentPatients.sort((a, b) => a.name.localeCompare(b.name));
              this.loading = false;
              this.patientDrawerTableDisabeld = false;
              this.patientDrawerSelectedToggle = 'department';
              this.toggleButtonData = [
                {
                  id: 'department',
                  width: '0',
                  name: this.languageService.translate('general.words.department'),
                  selected: true,
                },
                {
                  id: 'all',
                  width: '0',
                  name: this.languageService.translate('epd.menu.all-patient', {
                    PATIENT_LABEL: this.activeCareInstitution?.patientTitlePlural,
                  }),
                },
              ];
            },
            err => this.errorService.onError(err)
          );
        }
        break;
    }
  }

  private async loadAllPatients(): Promise<void> {
    const { data } = await this.patientService.getPatients(false);
    this.loading = false;
    this.patientDrawerTableDisabeld = false;
    if (!data) {
      return;
    }
    this.patientDrawerColumns = this.patientDrawerAllColumns;
    this.departmentPatients = (data.patients ?? []).map(p => new PatientListModel(p as Patient));
    this.departmentPatients.sort((a, b) => a.name.localeCompare(b.name));
    this.patientDrawerSelectedToggle = 'all';
    this.toggleButtonData = [
      {
        id: 'department',
        width: '0',
        name: this.languageService.translate('general.words.department'),
      },
      {
        id: 'all',
        width: '0',
        name: this.languageService.translate('epd.menu.all-patient', {
          PATIENT_LABEL: this.activeCareInstitution?.patientTitlePlural,
        }),
        selected: true,
      },
    ];
  }

  openPrint(event: any) {
    event.preventDefault();
    event.stopPropagation();
    this.sidenav.toggleDrawer();
    this.router.navigateByUrl(`/epd/patient/${this.activePatient!.id}/print`);
  }

  checkAiChatButtonVisibility() {
    if (this.activePatient == null) {
      this.aiChatButtonVisible = false;
      return;
    }
    if (this.authService.isUserTeacher()) {
      this.caseService.getAiChatCasesByPatientId(this.activePatient.id).subscribe({
        next: resp => {
          this.loading = false;
          this.aiCases = resp as Case[];
          if (!this.aiCases || this.aiCases.length < 1) {
            this.aiChatButtonVisible = false;
          } else {
            this.aiChatButtonVisible = true;
          }
        },
        error: err => {
          this.loading = false;
          this.errorService.onError(err);
        },
      });
    } else {
      const selectedCase = this.dataService.getSelectedCase();
      this.aiChatButtonVisible = !!(selectedCase.id && selectedCase.aiChatUrl);
      if (this.aiChatButtonVisible) {
        this.aiChatUrl = selectedCase.aiChatUrl;
      }
    }
  }

  aiChatUrl: string = '';
  aiCases: Case[] = [];
  aiChatOverlayOpen: boolean = false;

  openAiChat() {
    this.aiChatOverlayOpen = !this.aiChatOverlayOpen;
  }
}
